import { generateFillLayout } from './fill-layouts/generate-fill-layout';
import type { GenerateFillLayoutOptions, TileLayoutResult } from './tile-layout-types';

/** Default fill attempt budget when layout seed alone does not yield `stats.pass`. */
export const MAP_GEN_FILL_LAYOUT_DEFAULT_MAX_ATTEMPTS = 8;

export interface FillLayoutAttemptRecord {
  attemptIndex: number;
  seed: number;
  pass: boolean;
  reachabilityCarved: number;
  filledTileCount: number;
}

export interface FillLayoutRetrySummary {
  attempts: number;
  succeeded: boolean;
  seedsTried: readonly number[];
  totalReachabilityCarved: number;
  attemptRecords: readonly FillLayoutAttemptRecord[];
}

export interface FillLayoutWithRetriesResult {
  result: TileLayoutResult;
  summary: FillLayoutRetrySummary;
}

function mixFillAttemptSeed(baseSeed: number, attemptIndex: number): number {
  // eslint-disable-next-line no-bitwise
  return (baseSeed + (attemptIndex * 0x9E3779B9)) >>> 0;
}

function emptyFailedFillLayout(baseOptions: GenerateFillLayoutOptions): FillLayoutWithRetriesResult {
  const widthTiles = Math.max(0, Math.floor(baseOptions.widthTiles));
  const heightTiles = Math.max(0, Math.floor(baseOptions.heightTiles));
  return {
    result: {
      grid: new Uint8Array(widthTiles * heightTiles),
      stats: {
        layoutId: baseOptions.layoutId,
        seed: baseOptions.seed,
        widthTiles,
        heightTiles,
        fillTileValue: baseOptions.fillTileValue,
        budget: 0,
        minimumOpenTileGap: baseOptions.minimumOpenTileGap,
        minimumPlacementSizeTiles: baseOptions.minimumPlacementSizeTiles,
        maximumPlacementSizeTiles: baseOptions.maximumPlacementSizeTiles,
        lineTileCount: 0,
        scatterTileCount: 0,
        filledTileCount: 0,
        coverage: 0,
        coverageCap: baseOptions.fillTileCoverage,
        reachabilityCarved: 0,
        pass: false
      }
    },
    summary: {
      attempts: 0,
      succeeded: false,
      seedsTried: [],
      totalReachabilityCarved: 0,
      attemptRecords: []
    }
  };
}

/**
 * Runs {@link generateFillLayout} with incremented seeds until `stats.pass` or attempts exhaust.
 * Each attempt still applies in-layout reachability carving (`ensureBaseTilesReachable`).
 * `maxAttempts` of `0` skips generation and returns a failed empty summary (same meaning as other
 * TF map-build budgets of zero: fall through toward fallback rather than forcing one try).
 */
export function generateFillLayoutWithRetries(
  baseOptions: GenerateFillLayoutOptions,
  maxAttempts: number
): FillLayoutWithRetriesResult {
  const attempts = Math.max(0, Math.floor(maxAttempts));
  if (attempts === 0) {
    return emptyFailedFillLayout(baseOptions);
  }

  const seedsTried: number[] = [];
  const attemptRecords: FillLayoutAttemptRecord[] = [];
  let totalReachabilityCarved = 0;
  let lastResult: TileLayoutResult | null = null;

  for (let attemptIndex = 0; attemptIndex < attempts; attemptIndex++) {
    const seed = mixFillAttemptSeed(baseOptions.seed, attemptIndex);
    seedsTried.push(seed);
    const result = generateFillLayout({ ...baseOptions, seed });
    lastResult = result;
    totalReachabilityCarved += result.stats.reachabilityCarved;
    attemptRecords.push({
      attemptIndex,
      seed,
      pass: result.stats.pass,
      reachabilityCarved: result.stats.reachabilityCarved,
      filledTileCount: result.stats.filledTileCount
    });
    if (result.stats.pass) {
      return {
        result,
        summary: {
          attempts: attemptIndex + 1,
          succeeded: true,
          seedsTried,
          totalReachabilityCarved,
          attemptRecords
        }
      };
    }
  }

  if (!lastResult) {
    return emptyFailedFillLayout(baseOptions);
  }

  return {
    result: lastResult,
    summary: {
      attempts,
      succeeded: false,
      seedsTried,
      totalReachabilityCarved,
      attemptRecords
    }
  };
}
